/* eslint-disable @typescript-eslint/no-unused-vars */
// Note: several imports and helpers below are used only in the commented-out
// "Show Steps" / expandable history sections. Keep them so re-enabling is trivial.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Collapse, useTheme } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Text from "components/common/Typography/Text";
import type { CompletedRefresh } from "hooks/usePatientDataRefresh";
/* eslint-enable @typescript-eslint/no-unused-vars */

interface CompletedProcessItemProps {
  process: CompletedRefresh;
  onClose: () => void;
  onRetry?: () => void;
  // Whether this refresh is genuinely retryable. Decided by the parent (which owns the
  // kill switch) so this component stays presentational and does not re-derive the rule.
  isRetryable?: boolean;
  isLast: boolean;
}

// Helper function to calculate duration from history
// Prefers summing per-step duration_ms (from backend); falls back to first/last timestamp delta
const calculateDurationFromHistory = (history: any[]): number => {
  if (!history || history.length === 0) return 0;

  // Sum per-step durations when the backend provides them
  const summed = history.reduce((acc: number, entry: any) => {
    return acc + (typeof entry?.duration_ms === "number" && entry.duration_ms > 0 ? entry.duration_ms : 0);
  }, 0);
  if (summed > 0) return summed;

  try {
    // Fallback: use first/last timestamps
    const firstTimestamp = new Date(history[0].timestamp).getTime();
    const lastTimestamp = new Date(history[history.length - 1].timestamp).getTime();

    return Math.abs(lastTimestamp - firstTimestamp);
  } catch {
    return 0;
  }
};

// Helper function to format duration
const formatDuration = (milliseconds: number): string => {
  if (milliseconds === 0) return "< 1s";

  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return seconds > 0 ? `${seconds}s` : "< 1s";
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatStepDuration = (durationMs: number): string => {
  if (!durationMs || durationMs <= 0) return "";
  if (durationMs < 1000) return `${durationMs}ms`;

  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${totalSeconds}s`;
};

// Helper function to format relative time with short forms
const getRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffSec < 10) return "just now";
  if (diffSec < 60) return `${diffSec} sec ago`;
  if (diffMin === 1) return "1 min ago";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour === 1) return "1 hr ago";
  return `${diffHour} hr ago`;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const toTitleCase = (str: string) => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const CompletedProcessItem = ({ process, onClose, onRetry, isRetryable = false, isLast }: CompletedProcessItemProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false); // eslint-disable-line @typescript-eslint/no-unused-vars

  // Calculate actual duration from history timestamps
  const actualDuration = calculateDurationFromHistory(process.history);
  const displayDuration = actualDuration > 0 ? actualDuration : process.duration;

  const handleViewPatient = () => {
    navigate(`/patients/${process.patientId}`);
    onClose();
  };

  // Status icon and color
  const getStatusIcon = () => {
    switch (process.status) {
      case "success":
        return <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />;
      case "error":
        return <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 20 }} />;
      case "timeout":
        return <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 20 }} />;
      case "unknown":
        // Deliberately not warning-coloured: we stopped watching, we did not observe a failure.
        return <WarningIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />;
      default:
        return <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 20 }} />;
    }
  };

  const getStatusText = () => {
    switch (process.status) {
      case "success":
        return "Completed";
      case "error":
        return "Failed";
      case "timeout":
        return "Timed out";
      case "unknown":
        return "Still processing";
      default:
        return "Completed";
    }
  };

  return (
    <Box
      sx={{
        borderBottom: !isLast ? `1px solid ${theme.palette.divider}` : "none",
        pb: !isLast ? 1.25 : 0,
        mb: !isLast ? 1.25 : 0,
      }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        {/* Status Icon */}
        <Box sx={{ pt: 0.25 }}>{getStatusIcon()}</Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Patient Name */}
          <Text variant="body2" sx={{ fontWeight: 600, mb: 0.25, fontSize: "0.875rem" }}>
            {process.patientName}
          </Text>

          {/* Status and Duration */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 0.75 }}>
            <Text variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
              {getStatusText()} {getRelativeTime(process.completedAt)}
            </Text>
            {process.status === "success" && displayDuration > 0 && (
              <>
                <Text variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
                  •
                </Text>
                <Text variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.75rem" }}>
                  {formatDuration(displayDuration)}
                </Text>
              </>
            )}
          </Box>

          {/* Error message -- only when this really was a failure, never for a slow refresh */}
          {isRetryable && process.errorMessage && (
            <Text
              variant="caption"
              sx={{
                color: theme.palette.error.main,
                fontSize: "0.75rem",
                mb: 0.75,
                display: "block",
                wordBreak: "break-word",
              }}>
              {process.errorMessage.length > 100 ? `${process.errorMessage.slice(0, 100)}...` : process.errorMessage}
            </Text>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            {isRetryable && onRetry && (
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={onRetry}
                sx={{
                  textTransform: "none",
                  fontSize: "0.6875rem",
                  py: 0.25,
                  px: 1,
                  minHeight: "24px",
                }}>
                Retry Refresh
              </Button>
            )}

            <Button
              size="small"
              variant="outlined"
              onClick={handleViewPatient}
              sx={{
                textTransform: "none",
                fontSize: "0.6875rem",
                py: 0.25,
                px: 1,
                minHeight: "24px",
              }}>
              View Patient
            </Button>

            {/* Show Steps CTA hidden for now
            {process.history && process.history.length > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={toggleExpand}
                startIcon={isExpanded ? <ExpandMoreIcon sx={{ fontSize: "16px" }} /> : <ChevronRightIcon sx={{ fontSize: "16px" }} />}
                sx={{
                  textTransform: "none",
                  fontSize: "0.6875rem",
                  color: theme.palette.text.secondary,
                  py: 0.25,
                  px: 0.75,
                  minHeight: "24px",
                }}>
                {isExpanded ? "Hide" : "Show"} Steps ({process.history.length})
              </Button>
            )}
            */}
          </Box>

          {/* Expandable History — hidden for now, re-enable with Show Steps CTA above
          {process.history && process.history.length > 0 && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1, pl: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                {process.history
                  .slice()
                  .reverse()
                  .map((entry, idx) => {
                    const hasDuration = typeof entry.duration_ms === "number" && entry.duration_ms > 0;
                    const rightLabel = hasDuration
                      ? formatStepDuration(entry.duration_ms as number)
                      : getRelativeTime(new Date(entry.timestamp).getTime());
                    return (
                      <Box
                        key={`${entry.timestamp}-${idx}`}
                        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5 }}>
                        <Text variant="caption" sx={{ fontWeight: 500, flex: 1, maxWidth: "250px", wordBreak: "break-word", lineHeight: 1.2, fontSize: "0.6875rem" }}>
                          {toTitleCase(entry.status)}
                        </Text>
                        <Text variant="caption" sx={{ color: theme.palette.text.secondary, fontStyle: "italic", whiteSpace: "nowrap", flexShrink: 0, fontSize: "0.6875rem" }}>
                          {rightLabel}
                        </Text>
                      </Box>
                    );
                  })}
              </Box>
            </Collapse>
          )}
          */}
        </Box>
      </Box>
    </Box>
  );
};

export default CompletedProcessItem;
